import React, { useState, useEffect, useRef } from 'react';
import { Mail, Send, Clock, ExternalLink, Loader2, Paperclip, Edit2, Save, X } from 'lucide-react';
import { sanitizeHtml } from '../lib/sanitize';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import api from '../lib/api';
import { toast } from 'sonner';
import AttachmentPicker from './AttachmentPicker';
import DocumentLightbox from './DocumentLightbox';

/**
 * EmailPreview Component
 * 
 * Renders a styled email preview with:
 * - Subject line
 * - Body content with clickable links
 * - Email signature (if enabled)
 * - Attachment indicator
 * - EDIT functionality for body text
 * 
 * Props:
 * - subject: Email subject line
 * - bodyHtml: HTML content of the email
 * - toEmail: Recipient email
 * - toName: Recipient name
 * - showActions: Show action buttons (klaarzetten/verzenden)
 * - onQueue: Callback when "Klaarzetten" is clicked
 * - onSend: Callback when "Verzenden" is clicked
 * - loading: Show loading state
 * - className: Additional CSS classes
 * - compact: Use compact styling for tooltips
 * - hasAttachment: Whether email has PDF attachment
 * - attachmentName: Name of the PDF attachment
 * - editable: Allow editing the email body
 * - offerteId: ID of the offerte to save changes to
 * - onBodyChange: Callback when body is edited (receives new bodyHtml)
 */
const EmailPreview = ({ 
  subject = '', 
  bodyHtml = '', 
  toEmail = '',
  toName = '',
  ccEmails = [],
  showActions = false,
  onQueue,
  onSend,
  loading = false,
  className = '',
  compact = false,
  hasAttachment = true,
  attachmentName = '',
  editable = false,
  offerteId = null,
  offerteType = 'offerte', // 'offerte' (reiniging) of 'installation_offerte'
  onBodyChange = null,
  // Extra document bijlagen (foto's, legplan, datasheets) met checkboxes
  attachmentOptions = [],
  selectedAttachmentIds = [],
  onToggleAttachment = null,
  onPreviewAttachment = null
}) => {
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureEnabled, setSignatureEnabled] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedBody, setEditedBody] = useState('');
  const [saving, setSaving] = useState(false);

  // Fetch signature settings
  useEffect(() => {
    const fetchSignature = async () => {
      try {
        const res = await api.get('/email-signature');
        setSignatureUrl(res.data.signature_url || '');
        setSignatureEnabled(res.data.signature_enabled || false);
      } catch (error) {
        console.error('Error fetching signature:', error);
      }
    };
    fetchSignature();
  }, []);

  // Convert HTML to plain text for editing
  const htmlToPlainText = (html) => {
    if (!html) return '';
    // Replace <br> and </p> with newlines
    let text = html.replace(/<br\s*\/?>/gi, '\n');
    text = text.replace(/<\/p>/gi, '\n\n');
    // Remove all other HTML tags
    text = text.replace(/<[^>]+>/g, '');
    // Decode HTML entities
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    text = textarea.value;
    // Trim extra whitespace
    text = text.replace(/\n{3,}/g, '\n\n').trim();
    return text;
  };

  // Convert plain text back to HTML
  const plainTextToHtml = (text) => {
    if (!text) return '';
    // Escape HTML special characters
    let html = text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    // Convert newlines to paragraphs
    const paragraphs = html.split(/\n\n+/);
    html = paragraphs.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
    return html;
  };

  // Start editing
  const handleStartEdit = () => {
    setEditedBody(htmlToPlainText(bodyHtml));
    setIsEditing(true);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditedBody('');
  };

  // Save changes
  const handleSaveEdit = async () => {
    if (!offerteId) {
      // If no offerteId, just call the callback
      if (onBodyChange) {
        onBodyChange(plainTextToHtml(editedBody));
      }
      setIsEditing(false);
      toast.success('Email tekst bijgewerkt');
      return;
    }

    setSaving(true);
    try {
      const newBodyHtml = plainTextToHtml(editedBody);
      
      // Save to the offerte's custom email body (juiste endpoint per type)
      const endpoint = offerteType === 'installation_offerte'
        ? `/installation-offertes/${offerteId}`
        : `/offertes/${offerteId}`;
      await api.put(endpoint, {
        custom_email_body: newBodyHtml
      });
      
      // Call the callback to update local state
      if (onBodyChange) {
        onBodyChange(newBodyHtml);
      }
      
      setIsEditing(false);
      toast.success('Email tekst opgeslagen bij offerte');
    } catch (error) {
      console.error('Error saving email body:', error);
      toast.error('Kon email tekst niet opslaan');
    } finally {
      setSaving(false);
    }
  };

  // Process HTML to make links clickable and styled
  const processHtml = (html) => {
    if (!html) return '';
    
    // Replace href links to open in new tab and add styling
    let processed = html.replace(
      /<a\s+href="([^"]+)"([^>]*)>/gi,
      '<a href="$1" target="_blank" rel="noopener noreferrer" style="color: #ea580c; text-decoration: underline;"$2>'
    );
    
    // Style paragraphs with dark text for light background and force left alignment
    processed = processed.replace(
      /<p>/gi,
      '<p style="margin-bottom: 1em; line-height: 1.6; color: #1f2937; text-align: left;">'
    );
    
    // Force left alignment on all text-align styles
    processed = processed.replace(
      /text-align:\s*center/gi,
      'text-align: left'
    );
    processed = processed.replace(
      /text-align:\s*right/gi,
      'text-align: left'
    );
    
    return processed;
  };

  const cardClasses = compact 
    ? 'w-full max-w-full sm:max-w-lg bg-white border-gray-200 shadow-xl flex flex-col' 
    : 'w-full bg-white border-gray-200';

  return (
    <Card className={`${cardClasses} ${className}`}>
      {!compact && (
        <CardHeader className="pb-3 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-500" />
            <CardTitle className="text-base text-gray-900">Email Preview</CardTitle>
          </div>
        </CardHeader>
      )}
      
      <CardContent className={compact ? 'p-4 flex-1 overflow-y-auto overflow-x-hidden max-h-[350px]' : 'p-6 overflow-x-hidden'}>
        {/* To field */}
        {(toEmail || toName) && (
          <div className="mb-3 pb-3 border-b border-gray-200">
            <span className="text-xs text-gray-500">Aan: </span>
            <span className="text-sm text-gray-900 font-medium">
              {toName ? `${toName} <${toEmail}>` : toEmail}
            </span>
            {(ccEmails || []).length > 0 && (
              <div className="mt-1" data-testid="email-preview-cc">
                <span className="text-xs text-gray-500">CC: </span>
                <span className="text-xs text-gray-700">{ccEmails.join(', ')}</span>
              </div>
            )}
          </div>
        )}
        
        {/* Subject */}
        {subject && (
          <div className="mb-4 pb-3 border-b border-gray-200">
            <span className="text-xs text-gray-500">Onderwerp: </span>
            <span className="text-sm font-medium text-gray-900">{subject}</span>
          </div>
        )}
        
        {/* Attachment indicator - shown at top so user sees it immediately */}
        {hasAttachment && (
          <div className="mb-2 flex items-center gap-2 p-2 bg-orange-50 rounded-lg border border-orange-200">
            <Paperclip className="w-4 h-4 text-orange-600" />
            <span className="text-sm text-gray-700">
              {attachmentName || 'PDF bijlage'}
            </span>
            <Badge variant="secondary" className="ml-auto bg-orange-100 text-orange-700">PDF</Badge>
          </div>
        )}
        
        {/* Extra document bijlagen: thumbnails met checkbox (foto's, legplan, datasheets) */}
        {attachmentOptions.length > 0 && (
          <div className="mb-4 p-2 bg-blue-50 rounded-lg border border-blue-200 space-y-1.5" data-testid="email-attachment-options">
            <span className="text-xs font-medium text-blue-800">Extra bijlagen meesturen (klik = groot bekijken):</span>
            <AttachmentPicker
              documents={attachmentOptions}
              selectedIds={selectedAttachmentIds}
              onToggle={onToggleAttachment}
              onPreview={onPreviewAttachment}
              testIdPrefix="email-attachment"
            />
          </div>
        )}
        
        {/* Email Body */}
        {isEditing ? (
          <div className="space-y-3">
            <Textarea
              value={editedBody}
              onChange={(e) => setEditedBody(e.target.value)}
              className="min-h-[150px] text-sm text-gray-800 bg-white border-gray-300 focus:border-orange-500 focus:ring-orange-500"
              placeholder="Email tekst..."
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={handleSaveEdit}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                {saving ? (
                  <Loader2 className="w-3 h-3 animate-spin mr-1" />
                ) : (
                  <Save className="w-3 h-3 mr-1" />
                )}
                Opslaan
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                <X className="w-3 h-3 mr-1" />
                Annuleren
              </Button>
            </div>
          </div>
        ) : (
          <div className="relative group">
            {editable && (
              <Button
                size="sm"
                variant="ghost"
                onClick={handleStartEdit}
                className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 p-0 bg-white hover:bg-orange-50 border border-gray-200 shadow-sm"
                title="Email bewerken"
              >
                <Edit2 className="w-3 h-3 text-orange-600" />
              </Button>
            )}
            <div 
              className="prose prose-sm max-w-none text-sm text-gray-800 text-left overflow-x-hidden break-words"
              style={{ 
                fontFamily: 'system-ui, -apple-system, sans-serif',
                lineHeight: '1.6',
                textAlign: 'left',
                wordWrap: 'break-word',
                overflowWrap: 'break-word'
              }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(processHtml(bodyHtml)) }}
            />
          </div>
        )}
        
        {/* Signature */}
        {signatureEnabled && signatureUrl && (
          <div className="mt-6 pt-4 border-t border-gray-200">
            <img 
              src={signatureUrl} 
              alt="Email handtekening" 
              className="max-w-full h-auto max-h-32 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}
      </CardContent>
      
      {/* Action Buttons - Fixed at bottom */}
      {showActions && (
        <div className="p-4 border-t border-gray-200 flex gap-3 bg-gray-50">
          <Button 
            variant="outline" 
            size={compact ? 'sm' : 'default'}
            onClick={() => {
              const pendingHtml = isEditing && editedBody.trim() ? plainTextToHtml(editedBody) : null;
              onQueue?.(pendingHtml);
            }}
            disabled={loading}
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Clock className="w-4 h-4 mr-2" />
            )}
            Klaarzetten
          </Button>
          <Button 
            size={compact ? 'sm' : 'default'}
            onClick={() => {
              const pendingHtml = isEditing && editedBody.trim() ? plainTextToHtml(editedBody) : null;
              onSend?.(pendingHtml);
            }}
            disabled={loading}
            className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Verzenden
          </Button>
        </div>
      )}
    </Card>
  );
};

/**
 * EmailPreviewButton Component
 * 
 * A button with hover-triggered popup that shows email preview
 * with queue/send actions and edit functionality.
 */
export const EmailPreviewButton = ({
  offerte,
  templateType,
  onFetchPreview,
  previewData,
  loading = false,
  onQueue,
  onSend,
  sending = false,
  disabled = false,
  className = '',
  editable = true,
  onBodyChange = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localBodyHtml, setLocalBodyHtml] = useState(null);
  const [attachmentOptions, setAttachmentOptions] = useState([]);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState([]);
  const [lightboxDoc, setLightboxDoc] = useState(null); // Grote popup viewer voor bijlagen
  const timeoutRef = useRef(null);
  const containerRef = useRef(null);

  // Entity type voor documenten afleiden uit templateType
  const documentsEntityType = (templateType || '').includes('installatie') || (templateType || '').includes('installation')
    ? 'installation_offerte'
    : 'offerte';

  // Documenten (foto's, legplan, datasheets) ophalen wanneer preview opent
  useEffect(() => {
    if (!isOpen || !offerte?.id) return;
    let cancelled = false;
    api.get(`/documents?entity_type=${documentsEntityType}&entity_id=${offerte.id}`)
      .then((res) => {
        if (!cancelled) setAttachmentOptions(res.data || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, offerte?.id, documentsEntityType]);

  const toggleAttachment = (docId) => {
    setSelectedAttachmentIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  // Handle mouse enter with delay
  const handleMouseEnter = async () => {
    if (disabled) return;
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Fetch preview if not already loaded
    if (!previewData && onFetchPreview) {
      onFetchPreview(offerte, templateType);
    }
    
    // Show after small delay
    timeoutRef.current = setTimeout(() => {
      setIsOpen(true);
    }, 300);
  };

  const handleMouseLeave = () => {
    // Clear the show timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Hide after delay
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  const handlePopupEnter = () => {
    // Clear hide timeout when entering popup
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handlePopupLeave = () => {
    // Hide after delay
    timeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  // Handle body change from editing
  const handleBodyChange = (newBodyHtml) => {
    setLocalBodyHtml(newBodyHtml);
    if (onBodyChange) {
      onBodyChange(offerte, newBodyHtml);
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Use local body if edited, otherwise use preview data
  const displayBodyHtml = localBodyHtml || previewData?.body_html;

  return (
    <div 
      className={`relative inline-block ${className}`}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Button
        variant="ghost"
        size="sm"
        disabled={disabled || sending}
        className="flex items-center gap-2"
        data-testid={`email-preview-btn-${offerte?.id}`}
      >
        {sending ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Mail className="w-4 h-4" />
        )}
        <span className="hidden sm:inline">Email</span>
      </Button>
      
      {isOpen && (
        <div 
          className="fixed z-[100] w-[calc(100vw-2rem)] sm:w-[500px] max-w-[500px] max-h-[550px] overflow-y-auto shadow-2xl rounded-lg"
          style={{
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)'
          }}
          onMouseEnter={handlePopupEnter}
          onMouseLeave={handlePopupLeave}
        >
          {loading || !previewData ? (
            <Card className="bg-white border-gray-200 p-8">
              <div className="flex items-center justify-center gap-3">
                <Loader2 className="w-5 h-5 animate-spin text-orange-500" />
                <span className="text-sm text-gray-600">Email preview laden...</span>
              </div>
            </Card>
          ) : (
            <EmailPreview
              subject={previewData.subject}
              bodyHtml={displayBodyHtml}
              toEmail={previewData.to_email}
              toName={previewData.to_name}
              ccEmails={previewData.cc_emails || []}
              showActions={true}
              onQueue={(pendingHtml) => {
                const finalHtml = pendingHtml || displayBodyHtml;
                if (pendingHtml) handleBodyChange(pendingHtml);
                onQueue?.(offerte, templateType, finalHtml, selectedAttachmentIds);
                setIsOpen(false);
              }}
              onSend={(pendingHtml) => {
                const finalHtml = pendingHtml || displayBodyHtml;
                if (pendingHtml) handleBodyChange(pendingHtml);
                onSend?.(offerte, templateType, finalHtml, selectedAttachmentIds);
                setIsOpen(false);
              }}
              loading={sending}
              compact={true}
              hasAttachment={true}
              attachmentName={`Solar Engineering_${(offerte?.offerte_number || offerte?.factuur_number || '').replace(/\//g, '-')}_${offerte?.customer_name || ''}.pdf`}
              editable={editable}
              offerteId={offerte?.id}
              offerteType={documentsEntityType}
              onBodyChange={handleBodyChange}
              attachmentOptions={attachmentOptions}
              selectedAttachmentIds={selectedAttachmentIds}
              onToggleAttachment={toggleAttachment}
              onPreviewAttachment={setLightboxDoc}
            />
          )}
        </div>
      )}
      
      {/* Grote popup viewer voor bijlage-thumbnails (blijft open ook als preview sluit) */}
      <DocumentLightbox
        open={!!lightboxDoc}
        onOpenChange={(o) => { if (!o) setLightboxDoc(null); }}
        document={lightboxDoc}
      />
    </div>
  );
};

/**
 * EmailPreviewTooltip Component (Legacy - kept for compatibility)
 */
export const EmailPreviewTooltip = ({
  children,
  subject,
  bodyHtml,
  toEmail,
  toName,
  onQueue,
  onSend,
  loading = false,
  disabled = false,
  hasAttachment = true,
  attachmentName = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnter = () => {
    if (disabled) return;
    setIsHovering(true);
    setTimeout(() => {
      if (isHovering) setIsOpen(true);
    }, 200);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    setTimeout(() => {
      if (!isHovering) setIsOpen(false);
    }, 300);
  };

  return (
    <div 
      className="relative inline-block"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      
      {isOpen && (
        <div 
          className="fixed sm:absolute z-50 sm:bottom-full left-1/2 transform -translate-x-1/2 sm:mb-2 top-1/2 sm:top-auto -translate-y-1/2 sm:translate-y-0 w-[calc(100vw-2rem)] sm:w-auto max-w-[500px]"
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={handleMouseLeave}
        >
          <EmailPreview
            subject={subject}
            bodyHtml={bodyHtml}
            toEmail={toEmail}
            toName={toName}
            showActions={true}
            onQueue={() => {
              onQueue?.();
              setIsOpen(false);
            }}
            onSend={() => {
              onSend?.();
              setIsOpen(false);
            }}
            loading={loading}
            compact={true}
            hasAttachment={hasAttachment}
            attachmentName={attachmentName}
          />
          <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-2">
            <div className="w-4 h-4 bg-white border-r border-b border-gray-200 transform rotate-45" />
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailPreview;
